import { useState, useEffect, useRef } from 'react';

export function useWebSocket(patientID, sessionID, onConnect) {
  const [socket, setSocket] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const socketRef = useRef(null);
  const isConnectingRef = useRef(false);
  const shouldReconnectRef = useRef(true);
  const initialSessionIDRef = useRef(null);

  useEffect(() => {
    if (!patientID) return;
    patientID = 'patients-0K8UC8642KEPY7UTSWpAv7DlFKX'; // TEMP OVERRIDE

    if (initialSessionIDRef.current === null) {
      initialSessionIDRef.current = sessionID || null;
    }

    // Determine WebSocket URL
    //
    // REACT_APP_WS_URL may be either:
    //   - a host origin (e.g. ws://localhost:8181 OR wss://dev.hcbot.rcp.care)
    //   - a full websocket endpoint (e.g. wss://dev.hcbot.rcp.care/api/v1/ws)
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let wsBase = process.env.REACT_APP_WS_URL;
    if (!wsBase) {
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        wsBase = `${wsProtocol}//${hostname}:8181`;
      } else {
        wsBase = `${wsProtocol}//${window.location.host}`;
      }
    }

    let url;
    try {
      url = new URL(wsBase);
    } catch (e) {
      // Fallback if someone sets REACT_APP_WS_URL to just a host:port
      url = new URL(`${wsProtocol}//${wsBase}`);
    }

    // If REACT_APP_WS_URL didn't include a path, default to our WS endpoint.
    const p = (url.pathname || '/').replace(/\/+$/, '');
    if (p === '' || p === '/') {
      url.pathname = '/api/v1/ws';
    }

    url.searchParams.set('patient_id', patientID);
    if (initialSessionIDRef.current) {
      url.searchParams.set('session_id', initialSessionIDRef.current);
    }
    const wsURL = url.toString();

    const connect = () => {
      // Prevent multiple simultaneous connection attempts
      if (isConnectingRef.current) {
        console.log('[WebSocket] already connecting, skipping...');
        return;
      }

      // Don't reconnect if we've exceeded max attempts or shouldReconnect is false
      if (!shouldReconnectRef.current) {
        console.log('[WebSocket] reconnection disabled');
        return;
      }

      try {
        isConnectingRef.current = true;
        console.log('[WebSocket] connecting to', wsURL);
        const ws = new WebSocket(wsURL);

        ws.onopen = () => {
          console.log('[WebSocket] connected');
          isConnectingRef.current = false;
          setConnectionStatus('connected');
          reconnectAttempts.current = 0;
          socketRef.current = ws;
          setSocket(ws);

          // Immediately authenticate on every connection.
          // Backend expects a raw JWT (no "Bearer ") and requires auth within ~10s.
          const normalizeToken = (t) => {
            const s = (t || '').trim();
            if (!s) return '';
            if (s.toLowerCase().startsWith('bearer ')) return s.slice(7).trim();
            return s;
          };

          const sendAuthIfPossible = () => {
            try {
              const raw = localStorage.getItem('kc_token');
              const kcToken = normalizeToken(raw);
              if (!kcToken) return false;
              const authMsg = { type: 'auth', kc_token: kcToken };
              ws.send(JSON.stringify(authMsg));
              console.log('[WebSocket] sent auth token');
              return true;
            } catch (e) {
              console.warn('[WebSocket] failed to send auth token', e);
              return false;
            }
          };

          // In some flows, AppLink resolution populates kc_token after WS is opened.
          // Poll briefly so we still authenticate within the server's 10s window.
          if (!sendAuthIfPossible()) {
            console.warn('[WebSocket] no kc_token yet; will retry briefly');
            const start = Date.now();
            const interval = setInterval(() => {
              if (ws.readyState !== WebSocket.OPEN) {
                clearInterval(interval);
                return;
              }
              if (sendAuthIfPossible()) {
                clearInterval(interval);
                return;
              }
              if (Date.now() - start > 9500) {
                clearInterval(interval);
                console.warn('[WebSocket] auth token still missing after 9.5s');
              }
            }, 250);
          }
          
          // Extract session ID from initial message if provided
          // The server will send a 'connected' message with session_id
        };

        ws.onclose = (event) => {
          console.log('[WebSocket] disconnected', {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean
          });
          isConnectingRef.current = false;
          socketRef.current = null;
          setSocket(null);
          setConnectionStatus('disconnected');
          
          // If the server closed us due to auth not being provided, don't thrash.
          // Let the UI/app-link flow populate kc_token, then a refresh or explicit
          // user action can reconnect.
          // Only treat this as auth_required if the server explicitly says so.
          // Some proxies / servers will use close code 1008 for other policies,
          // and browsers may omit close reason strings.
          const authRequiredClose = (String(event.reason || '').includes('auth_required'));
          if (authRequiredClose) {
            console.warn('[WebSocket] server closed connection: auth_required; not auto-reconnecting');
            shouldReconnectRef.current = false;
            setConnectionStatus('error');
            return;
          }

          // Only reconnect if it wasn't a manual close and we should reconnect
          if (shouldReconnectRef.current && reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current++;
            // Use exponential backoff with minimum 2 second delay
            const delay = Math.max(2000, Math.min(1000 * Math.pow(2, reconnectAttempts.current - 1), 30300));
            console.log(`[WebSocket] will reconnect in ${delay}ms (attempt ${reconnectAttempts.current})`);
            reconnectTimeoutRef.current = setTimeout(() => {
              if (shouldReconnectRef.current) {
                connect();
              }
            }, delay);
          } else if (reconnectAttempts.current >= maxReconnectAttempts) {
            console.log('[WebSocket] max reconnection attempts reached');
            setConnectionStatus('error');
          }
        };

        ws.onerror = (error) => {
          console.error('[WebSocket] error:', error);
          isConnectingRef.current = false;
          setConnectionStatus('error');
        };

        socketRef.current = ws;
        setSocket(ws);
      } catch (error) {
        console.error('Failed to create WebSocket:', error);
        isConnectingRef.current = false;
        setConnectionStatus('error');
      }
    };

    shouldReconnectRef.current = true;
    connect();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      setSocket(null);
    };
  }, [patientID]);

  const sendMessage = (message) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      console.log('[WebSocket] sending message', message);
      socket.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected');
    }
  };

  return {
    socket,
    sendMessage,
    connectionStatus,
  };
}
